import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { helpSections, helpLocationFor } from './helpContent.js';
import './help.scss';

/**
 * Slide-in help drawer: section tabs (Nav) + topic pills + scrollable HTML
 * content pane. Opens on the topic most relevant to the current route, closes
 * on Escape or backdrop click, and locks body scroll while open.
 */
export default function HelpDrawer({ open, onClose }) {
  const location = useLocation();
  const initial = useMemo(() => helpLocationFor(location.pathname), [location.pathname]);

  const [activeSectionId, setActiveSectionId] = useState(initial.sectionId);
  const [activeTopicId, setActiveTopicId] = useState(initial.topicId);

  // When the drawer is (re)opened, jump to the route-relevant topic.
  useEffect(() => {
    if (open) {
      setActiveSectionId(initial.sectionId);
      setActiveTopicId(initial.topicId);
    }
  }, [open, initial]);

  // Escape-to-close + body-scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const activeSection =
    helpSections.find((s) => s.id === activeSectionId) || helpSections[0];
  const activeTopic =
    activeSection.topics.find((t) => t.id === activeTopicId) || activeSection.topics[0];

  const selectSection = (section) => {
    setActiveSectionId(section.id);
    setActiveTopicId(section.topics[0]?.id);
  };

  return (
    <div className="help-overlay" role="presentation">
      <button type="button" className="help-backdrop border-0 p-0" aria-label="Close help" onClick={onClose} />
      <aside
        className="help-drawer bg-white shadow-lg d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-label="Help"
      >
        <header className="help-drawer-header d-flex align-items-center justify-content-between px-3 py-2 bg-primary text-white">
          <span className="fw-semibold">
            <i className="fa fa-circle-question me-2" aria-hidden="true" />
            Help &amp; Guides
          </span>
          <button
            type="button"
            className="btn-close btn-close-white"
            aria-label="Close help"
            onClick={onClose}
          />
        </header>

        {/* Section tabs */}
        <nav className="help-nav nav nav-tabs px-2 pt-2 flex-nowrap overflow-auto" aria-label="Help sections">
          {helpSections.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`nav-link text-nowrap ${section.id === activeSection.id ? 'active' : ''}`}
              onClick={() => selectSection(section)}
            >
              <i className={`${section.icon} me-1`} aria-hidden="true" />
              {section.title}
            </button>
          ))}
        </nav>

        {/* Topic pills */}
        {activeSection.topics.length > 1 && (
          <div className="help-topics d-flex flex-wrap gap-2 px-3 pt-3">
            {activeSection.topics.map((topic) => (
              <button
                key={topic.id}
                type="button"
                className={`btn btn-sm rounded-pill ${
                  topic.id === activeTopic.id ? 'btn-primary' : 'btn-outline-primary'
                }`}
                onClick={() => setActiveTopicId(topic.id)}
              >
                {topic.title}
              </button>
            ))}
          </div>
        )}

        {/* Content pane */}
        <div className="help-content flex-grow-1 overflow-auto px-3 py-3">
          <h6 className="fw-bold mb-3">{activeTopic.title}</h6>
          <div
            className="help-body"
            // Content is authored HTML from the in-repo help data module.
            dangerouslySetInnerHTML={{ __html: activeTopic.body }}
          />
        </div>
      </aside>
    </div>
  );
}
